import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  AlertTitle,
  Grid,
  TextField,

  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  ExpandMore as ExpandMoreIcon,
  MyLocation as MyLocationIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import apiClient from '../../../../../../utils/apiClient';

const AddressManagement = ({ workflowData, onDataUpdate, onNext, onBack, isValid }) => {
  const { enqueueSnackbar } = useSnackbar();
  
  // Local state
  const [splits, setSplits] = useState(workflowData.splits || []);
  const [validationResults, setValidationResults] = useState({});
  const [isValidating, setIsValidating] = useState({});

  // US States for dropdown
  const states = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'
  ];

  // Initialize addresses for splits once on mount.
  // Using an empty dependency array intentionally — this component owns `splits`
  // from this point forward and syncs back to the parent via onDataUpdate.
  // Listening to workflowData.splits here would create a cycle: every keystroke
  // triggers onDataUpdate → parent re-renders → this effect fires → setSplits
  // again → focus is lost on every character typed.
  useEffect(() => {
    if (workflowData.splits && workflowData.splits.length > 0) {
      const updatedSplits = workflowData.splits.map(split => ({
        ...split,
        address: split.address || {
          street: '',
          city: '',
          state: '',
          zip: '',
          coordinates: null,
          formatted_address: null
        }
      }));
      setSplits(updatedSplits);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync local splits back to parent workflow state whenever they change
  useEffect(() => {
    onDataUpdate({ splits });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [splits]);

  // Handle address field changes
  const handleAddressChange = (splitIndex, field, value) => {
    setSplits(prev => prev.map((split, index) => {
      if (index === splitIndex) {
        return {
          ...split,
          address: {
            ...split.address,
            [field]: value
          }
        };
      }
      return split;
    }));

    // Clear validation results when address changes
    if (validationResults[splitIndex]) {
      setValidationResults(prev => {
        const updated = { ...prev };
        delete updated[splitIndex];
        return updated;
      });
    }
  };

  // Validate address with geocoding
  const validateAddress = async (splitIndex) => {
    const split = splits[splitIndex];
    const address = split.address;

    if (!address.street || !address.city || !address.state || !address.zip) {
      enqueueSnackbar('Please fill in all address fields before validating', { variant: 'warning' });
      return;
    }

    setIsValidating(prev => ({ ...prev, [splitIndex]: true }));

    try {
      // Use the splits test endpoint to validate geocoding
      const fullAddress = `${address.street}, ${address.city}, ${address.state} ${address.zip}`;
      
      const response = await apiClient.post('/v2/splits/test', {
        identifier: workflowData.parentNcessch,
        split_config: {},
        addresses: {
          [split.suffix]: {
            address: fullAddress,
            street: address.street,
            city: address.city,
            state: address.state,
            zip: address.zip
          }
        }
      });

      if (response.geocoding?.[split.suffix]) {
        const geocodingResult = response.geocoding[split.suffix];
        
        if (geocodingResult.success) {
          // Update split with validated address data
          setSplits(prev => prev.map((s, index) => {
            if (index === splitIndex) {
              return {
                ...s,
                address: {
                  ...s.address,
                  coordinates: {
                    lat: geocodingResult.coordinates.latitude,
                    lng: geocodingResult.coordinates.longitude
                  },
                  formatted_address: geocodingResult.formatted_address,
                  address_components: geocodingResult.address_components
                }
              };
            }
            return s;
          }));

          setValidationResults(prev => ({
            ...prev,
            [splitIndex]: {
              success: true,
              coordinates: {
                lat: geocodingResult.coordinates.latitude,
                lng: geocodingResult.coordinates.longitude
              },
              formatted_address: geocodingResult.formatted_address
            }
          }));

          enqueueSnackbar(`Address validated for ${split.displayName}`, { variant: 'success' });
        } else {
          setValidationResults(prev => ({
            ...prev,
            [splitIndex]: {
              success: false,
              error: geocodingResult.error || 'Geocoding failed'
            }
          }));

          enqueueSnackbar(`Address validation failed for ${split.displayName}`, { variant: 'error' });
        }
      }
    } catch (error) {
      console.error('Error validating address:', error);
      setValidationResults(prev => ({
        ...prev,
        [splitIndex]: {
          success: false,
          error: 'Failed to validate address'
        }
      }));
      enqueueSnackbar('Error validating address', { variant: 'error' });
    } finally {
      setIsValidating(prev => ({ ...prev, [splitIndex]: false }));
    }
  };

  // Copy parent school address as a starting point for this split
  const copyParentAddress = (splitIndex) => {
    const parentAddress = workflowData.parentSchool?.address;

    if (!parentAddress || !parentAddress.street) {
      enqueueSnackbar('Parent school address is not available', { variant: 'warning' });
      return;
    }

    setSplits(prev => prev.map((split, index) => {
      if (index === splitIndex) {
        return {
          ...split,
          address: {
            street: parentAddress.street || '',
            city: parentAddress.city || '',
            state: parentAddress.state || '',
            zip: parentAddress.zip || '',
            coordinates: null,
            formatted_address: null
          }
        };
      }
      return split;
    }));

    enqueueSnackbar('Parent school address copied', { variant: 'info' });
  };

  // Check if all addresses are valid
  const allAddressesValid = () => {
    return splits.every((split, index) => {
      const address = split.address;
      return address && address.street && address.city && address.state && address.zip &&
             validationResults[index]?.success;
    });
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LocationIcon color="primary" />
        Assign Addresses to Split Schools
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Each split school needs its own address. Addresses will be validated using geocoding services.
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        <AlertTitle>Address Requirements</AlertTitle>
        Each split school must have a complete, valid address that can be geocoded. This is required for:
        <ul>
          <li>ESRI demographic data collection</li>
          <li>Nearby schools analysis</li>
          <li>Geographic mapping and visualization</li>
        </ul>
      </Alert>

      {/* Address Forms for Each Split */}
      {splits.map((split, splitIndex) => (
        <Card key={split.id || splitIndex} sx={{ mb: 3 }}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                <Typography variant="h6">
                  {split.displayName}
                </Typography>
                <Chip 
                  label={`Grades: ${split.gradesServed?.join(', ')}`} 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                />
                
                {/* Validation Status */}
                {validationResults[splitIndex] && (
                  <Chip
                    icon={validationResults[splitIndex].success ? <CheckIcon /> : <ErrorIcon />}
                    label={validationResults[splitIndex].success ? 'Address Validated' : 'Validation Failed'}
                    color={validationResults[splitIndex].success ? 'success' : 'error'}
                    size="small"
                  />
                )}
                
                {isValidating[splitIndex] && (
                  <CircularProgress size={20} />
                )}
              </Box>
            </AccordionSummary>
            
            <AccordionDetails>
              <Grid container spacing={3}>
                {/* Street Address */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Street Address"
                    value={split.address?.street || ''}
                    onChange={(e) => handleAddressChange(splitIndex, 'street', e.target.value)}
                    placeholder="123 Main Street"
                    required
                  />
                </Grid>

                {/* City, State, ZIP */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="City"
                    value={split.address?.city || ''}
                    onChange={(e) => handleAddressChange(splitIndex, 'city', e.target.value)}
                    placeholder="City"
                    required
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth required>
                    <InputLabel>State</InputLabel>
                    <Select
                      value={split.address?.state || ''}
                      onChange={(e) => handleAddressChange(splitIndex, 'state', e.target.value)}
                      label="State"
                    >
                      {states.map(state => (
                        <MenuItem key={state} value={state}>
                          {state}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="ZIP Code"
                    value={split.address?.zip || ''}
                    onChange={(e) => handleAddressChange(splitIndex, 'zip', e.target.value)}
                    placeholder="12345"
                    required
                  />
                </Grid>

                {/* Action Buttons */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      onClick={() => copyParentAddress(splitIndex)}
                      size="small"
                    >
                      Copy Parent Address
                    </Button>
                    
                    <Button
                      variant="contained"
                      onClick={() => validateAddress(splitIndex)}
                      disabled={isValidating[splitIndex] || 
                               !split.address?.street || 
                               !split.address?.city || 
                               !split.address?.state || 
                               !split.address?.zip}
                      startIcon={isValidating[splitIndex] ? <CircularProgress size={16} /> : <MyLocationIcon />}
                      size="small"
                    >
                      {isValidating[splitIndex] ? 'Validating...' : 'Validate Address'}
                    </Button>
                  </Box>
                </Grid>

                {/* Validation Results */}
                {validationResults[splitIndex] && (
                  <Grid item xs={12}>
                    {validationResults[splitIndex].success ? (
                      <Alert severity="success">
                        <AlertTitle>Address Validated</AlertTitle>
                        <Typography variant="body2">
                          <strong>Formatted Address:</strong> {validationResults[splitIndex].formatted_address}
                        </Typography>
                        {validationResults[splitIndex].coordinates && 
                         validationResults[splitIndex].coordinates.lat && 
                         validationResults[splitIndex].coordinates.lng && (
                          <Typography variant="body2">
                            <strong>Coordinates:</strong> {' '}
                            {validationResults[splitIndex].coordinates.lat.toFixed(6)}, {' '}
                            {validationResults[splitIndex].coordinates.lng.toFixed(6)}
                          </Typography>
                        )}
                      </Alert>
                    ) : (
                      <Alert severity="error">
                        <AlertTitle>Address Validation Failed</AlertTitle>
                        <Typography variant="body2">
                          {validationResults[splitIndex].error}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          Please check the address and try again.
                        </Typography>
                      </Alert>
                    )}
                  </Grid>
                )}
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Card>
      ))}

      {/* Validation Summary */}
      {splits.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Address Validation Summary
            </Typography>
            
            <Grid container spacing={2}>
              {splits.map((split, index) => {
                const hasAddress = split.address?.street && split.address?.city && 
                                 split.address?.state && split.address?.zip;
                const isValidated = validationResults[index]?.success;
                
                return (
                  <Grid key={index} item xs={12} md={6}>
                    <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box>
                        {isValidated ? (
                          <CheckIcon color="success" />
                        ) : hasAddress ? (
                          <WarningIcon color="warning" />
                        ) : (
                          <ErrorIcon color="error" />
                        )}
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle2">
                          {split.displayName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {isValidated ? 'Address validated' : 
                           hasAddress ? 'Address complete, needs validation' : 
                           'Address incomplete'}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Completion Requirements */}
      {!allAddressesValid() && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle>Complete Address Validation Required</AlertTitle>
          All split schools must have complete, validated addresses before proceeding to the next step.
        </Alert>
      )}

      {/* Next Step Button */}
      {allAddressesValid() && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            onClick={onNext}
            size="large"
          >
            Continue to Team Assignment
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default AddressManagement;
